import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  StopOutlined
} from '@ant-design/icons';

import { TOrbit } from './i18n/TModules';
import { OrbitActionOutcome } from './l4/types';
import { OrbitFailureCategory, OrbitOutcome, OrbitTurn } from './types';

export type OrbitOutcomeTone = 'muted' | 'negative';

export interface OrbitOutcomeCopy {
  labelKey: string;
  Icon: typeof QuestionCircleOutlined;
  tone: OrbitOutcomeTone;
  /** Second line under the label. Only the PLA-9581 failure categories carry one. */
  bodyKey?: string;
}

type LabelledOutcome = Exclude<OrbitOutcome, 'answered' | OrbitActionOutcome> | 'timeout';

const OUTCOME_COPY: Record<LabelledOutcome, OrbitOutcomeCopy> = {
  clarify: { labelKey: `${TOrbit}.outcome.clarify`, Icon: QuestionCircleOutlined, tone: 'muted' },
  unsupported: { labelKey: `${TOrbit}.outcome.unsupported`, Icon: StopOutlined, tone: 'muted' },
  permission_restricted: {
    labelKey: `${TOrbit}.outcome.permissionRestricted`,
    Icon: LockOutlined,
    tone: 'muted'
  },
  insufficient_data: {
    labelKey: `${TOrbit}.outcome.insufficientData`,
    Icon: DatabaseOutlined,
    tone: 'muted'
  },
  /*
    PLA-9579: its own row, deliberately NOT a synonym of `insufficient_data`. That one means the
    metric ran and returned too little; this one means there is nothing set up to run. The gear
    separates them at a glance. The tone stays `muted` on purpose - `negative` is the panel's red
    for a failure the reader should act on, and an unconfigured metric is neither a failure nor
    theirs to fix.
  */
  not_configured: {
    labelKey: `${TOrbit}.outcome.notConfigured`,
    Icon: SettingOutlined,
    tone: 'muted'
  },
  report_queued: {
    labelKey: `${TOrbit}.outcome.reportQueued`,
    Icon: FileTextOutlined,
    tone: 'muted'
  },
  error: { labelKey: `${TOrbit}.outcome.error`, Icon: CloseCircleOutlined, tone: 'negative' },
  timeout: { labelKey: `${TOrbit}.outcome.timeout`, Icon: ClockCircleOutlined, tone: 'negative' }
};

const isLabelledOutcome = (
  outcome: OrbitOutcome
): outcome is Extract<LabelledOutcome, OrbitOutcome> =>
  Object.prototype.hasOwnProperty.call(OUTCOME_COPY, outcome);

/*
  PLA-9581: which service was down, for the two categories where the answer is not "Orbit broke".
  `internal` is deliberately absent - it IS the generic failure, so it resolves through the same
  `?? OUTCOME_COPY.error` fallback that a missing or unrecognised category takes. Icon and tone stay
  the generic ones: the reader's action (Retry) has not changed, only the explanation.
*/
const FAILURE_CATEGORY_COPY: Partial<Record<OrbitFailureCategory, OrbitOutcomeCopy>> = {
  upstream_data: {
    labelKey: `${TOrbit}.outcome.errorUpstreamData`,
    bodyKey: `${TOrbit}.outcome.errorUpstreamDataBody`,
    Icon: CloseCircleOutlined,
    tone: 'negative'
  },
  upstream_llm: {
    labelKey: `${TOrbit}.outcome.errorUpstreamLlm`,
    bodyKey: `${TOrbit}.outcome.errorUpstreamLlmBody`,
    Icon: CloseCircleOutlined,
    tone: 'negative'
  }
};

// `timeout` is a state, not an outcome (contract §3): the worker died and `outcome` stays null.
export const getOrbitOutcomeCopy = (
  turn: Pick<OrbitTurn, 'state' | 'outcome' | 'failure_category'>
): OrbitOutcomeCopy | null => {
  if (turn.state === 'timeout') {
    return OUTCOME_COPY.timeout;
  }
  if (turn.state === 'failed') {
    // `?? 'internal'` only routes null / undefined at the generic row; a value outside the union
    // misses the map at runtime and takes the same `?? OUTCOME_COPY.error` exit.
    return FAILURE_CATEGORY_COPY[turn.failure_category ?? 'internal'] ?? OUTCOME_COPY.error;
  }
  if (turn.state !== 'completed' || turn.outcome === null || turn.outcome === 'answered') {
    return null;
  }
  // An outcome this build has no copy for reads as the generic failure, never as an empty reply.
  return isLabelledOutcome(turn.outcome) ? OUTCOME_COPY[turn.outcome] : OUTCOME_COPY.error;
};
